import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getLocalizedValue } from '../../constants/Languages';

const actionButtonSx = {
  width: { xs: 40, sm: 32 },
  height: { xs: 40, sm: 32 },
};

const ServiceCategoryCard = ({
  category,
  dragHandleProps,
  loading,
  canEdit,
  canDelete,
  isFirst,
  isLast,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
}) => {
  const { t, i18n } = useTranslation();
  const localizedName = getLocalizedValue(
    category.name,
    i18n.language,
    undefined,
    t('serviceCategoriesManagement.card.unnamed')
  );
  const localizedDescription = getLocalizedValue(category.description, i18n.language, undefined, '');
  const serviceCount = category.serviceIds?.length || 0;
  const hasActions = canEdit || canDelete;

  const reorderActions = canEdit ? (
    <>
      <Tooltip title={t('serviceCategoriesManagement.actions.moveUp')}>
        <span>
          <IconButton
            size="small"
            onClick={onMoveUp}
            disabled={loading || isFirst}
            sx={actionButtonSx}
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t('serviceCategoriesManagement.actions.moveDown')}>
        <span>
          <IconButton
            size="small"
            onClick={onMoveDown}
            disabled={loading || isLast}
            sx={actionButtonSx}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </>
  ) : null;

  const manageActions = (
    <>
      {canEdit ? (
        <Tooltip title={t('serviceCategoriesManagement.actions.edit')}>
          <IconButton
            size="small"
            onClick={onEdit}
            disabled={loading}
            sx={{ ...actionButtonSx, color: '#1976d2' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {canDelete ? (
        <Tooltip title={t('serviceCategoriesManagement.actions.delete')}>
          <IconButton
            size="small"
            onClick={onDelete}
            disabled={loading}
            sx={{ ...actionButtonSx, color: '#d32f2f' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </>
  );

  return (
    <Card
      sx={{
        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
        opacity: category.isActive ? 1 : 0.85,
        '@media (hover: hover)': {
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
          },
        },
      }}
    >
      <CardContent sx={{ p: { xs: 1.75, sm: 3 }, '&:last-child': { pb: { xs: 1.75, sm: 3 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 0.5, sm: 1.5 } }}>
          {canEdit ? (
            <Box
              {...dragHandleProps}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                cursor: 'grab',
                flexShrink: 0,
                width: { xs: 40, sm: 'auto' },
                minHeight: { xs: 40, sm: 'auto' },
                ml: { xs: -0.5, sm: 0 },
                mt: { xs: 0, sm: 0.25 },
                touchAction: 'none',
              }}
              aria-label={t('serviceCategoriesManagement.actions.drag')}
            >
              <DragIndicatorIcon />
            </Box>
          ) : null}

          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.3,
                fontSize: { xs: '1.05rem', sm: '1.25rem' },
                wordBreak: 'break-word',
                mb: 0.5,
                pt: { xs: 0.5, sm: 0 },
              }}
            >
              {localizedName}
            </Typography>

            {localizedDescription ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 0.75,
                  display: '-webkit-box',
                  WebkitLineClamp: { xs: 2, sm: 3 },
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {localizedDescription}
              </Typography>
            ) : null}

            <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: hasActions ? 0.5 : 0 }}>
              <Chip
                size="small"
                label={category.isActive
                  ? t('serviceCategoriesManagement.status.active')
                  : t('serviceCategoriesManagement.status.inactive')}
                color={category.isActive ? 'success' : 'default'}
                variant="outlined"
              />
              <Chip
                size="small"
                label={t('serviceCategoriesManagement.card.serviceCount', { count: serviceCount })}
                variant="outlined"
              />
            </Box>

            {hasActions ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {reorderActions}
                {manageActions}
              </Box>
            ) : null}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ServiceCategoryCard;
